import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Brain,
  Loader2,
  Sparkles,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const TREND_CONFIG = {
  improving: { icon: TrendingUp, color: '#4B6651', bg: '#4B6651/10', label: 'Improving' },
  worsening: { icon: TrendingDown, color: '#D94F4F', bg: '#D94F4F/10', label: 'Worsening' },
  stable: { icon: Minus, color: '#5C5F5C', bg: '#F8F7F4', label: 'Stable' },
  baseline: { icon: Activity, color: '#4B6651', bg: '#4B6651/10', label: 'Baseline' }
};

const LesionProgression = ({ patientId }) => {
  const [progression, setProgression] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingProgression, setAnalyzingProgression] = useState(false);

  useEffect(() => {
    const fetchProgression = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/neuro/patients/${patientId}/progression`, { withCredentials: true });
        setProgression(data.progression || []);
        setSummary(data.summary);
      } catch (error) {
        logger.error('Error fetching progression:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgression();
  }, [patientId]);

  const handleAiProgression = async () => {
    setAnalyzingProgression(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/neuro/patients/${patientId}/progression-analysis`, {}, { withCredentials: true });
      setAiAnalysis(data.ai_analysis);
      toast.success('Progression analysis complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzingProgression(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  if (progression.length === 0) {
    return (
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-8 text-center">
          <Activity className="w-10 h-10 text-[#5C5F5C] mx-auto mb-3" />
          <p className="text-[#5C5F5C]">No lesion analyses recorded yet</p>
          <p className="text-sm text-[#5C5F5C] mt-1">Complete neurological exams and run AI analysis to track lesion progression</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="lesion-progression">
      {/* Summary Card */}
      {summary && (
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#4B6651]" />
              Progression Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-[#F8F7F4] rounded-lg">
                <p className="text-xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{summary.total_analyses}</p>
                <p className="text-xs text-[#5C5F5C]">Analyses</p>
              </div>
              <div className="text-center p-3 bg-[#F8F7F4] rounded-lg">
                <p className="text-xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{summary.current_findings_count}</p>
                <p className="text-xs text-[#5C5F5C]">Current Findings</p>
              </div>
              <div className="text-center p-3 bg-[#F8F7F4] rounded-lg">
                <p className="text-xl font-semibold text-[#2A2C2A] font-['Work_Sans']">{summary.current_locations?.length || 0}</p>
                <p className="text-xs text-[#5C5F5C]">Lesion Locations</p>
              </div>
              <div className={`text-center p-3 rounded-lg ${
                summary.overall_trend === 'improving' ? 'bg-[#4B6651]/10' :
                summary.overall_trend === 'worsening' ? 'bg-[#D94F4F]/10' : 'bg-[#F8F7F4]'
              }`}>
                {(() => {
                  const trend = TREND_CONFIG[summary.overall_trend] || TREND_CONFIG.stable;
                  const TrendIcon = trend.icon;
                  return (
                    <>
                      <TrendIcon className="w-6 h-6 mx-auto mb-1" style={{ color: trend.color }} />
                      <p className="text-xs font-medium" style={{ color: trend.color }}>{trend.label}</p>
                    </>
                  );
                })()}
              </div>
            </div>

            {summary.current_locations?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-[#5C5F5C] uppercase mb-2">Current Lesion Locations</p>
                <div className="flex flex-wrap gap-2">
                  {summary.current_locations.map((loc, i) => (
                    <span key={`curr-loc-${loc}`} className="px-3 py-1 bg-[#4B6651] text-white rounded-full text-sm">{loc}</span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* AI Progression Analysis */}
      {progression.length >= 2 && (
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#C06144]" />
              AI Progression Analysis
              <span className="ml-1 px-2 py-0.5 bg-[#C06144]/10 text-[#C06144] rounded text-xs font-medium">GPT-5.2</span>
            </CardTitle>
            <Button
              onClick={handleAiProgression}
              disabled={analyzingProgression}
              className="bg-[#C06144] hover:bg-[#A84F38] text-white"
              data-testid="ai-progression-btn"
            >
              {analyzingProgression ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 mr-2" />
              )}
              {analyzingProgression ? 'Analyzing...' : 'Analyze Progression'}
            </Button>
          </CardHeader>
          {aiAnalysis && (
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-[#2A2C2A] leading-relaxed text-sm">{aiAnalysis}</div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Progression Timeline */}
      <Card className="border-[#E5E4E0] bg-white">
        <CardHeader>
          <CardTitle className="text-lg font-['Work_Sans']">Lesion Analysis History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {progression.map((entry, idx) => {
              const trend = TREND_CONFIG[entry.trend] || null;
              const TrendIcon = trend?.icon || Minus;
              return (
                <div key={entry.id} className="flex gap-4" data-testid={`progression-entry-${idx}`}>
                  {/* Visual indicator */}
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                      entry.trend === 'improving' ? 'bg-[#4B6651]' :
                      entry.trend === 'worsening' ? 'bg-[#D94F4F]' : 'bg-[#5C5F5C]'
                    }`}>
                      {entry.index}
                    </div>
                    {idx < progression.length - 1 && <div className="flex-1 w-0.5 bg-[#E5E4E0] mt-1" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-[#2A2C2A]">
                        {entry.date ? new Date(entry.date).toLocaleDateString() : 'Unknown'}
                      </span>
                      {trend && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded" style={{ color: trend.color, backgroundColor: `${trend.color}15` }}>
                          <TrendIcon className="w-3 h-3" />
                          {trend.label}
                        </span>
                      )}
                    </div>

                    {/* Locations */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {entry.locations.map((loc, i) => (
                        <span key={`hist-loc-${loc}`} className="px-2 py-0.5 bg-[#4B6651]/10 text-[#4B6651] rounded text-xs">{loc}</span>
                      ))}
                    </div>

                    {/* Findings count */}
                    <p className="text-xs text-[#5C5F5C]">
                      {entry.abnormal_findings.length} abnormal finding{entry.abnormal_findings.length !== 1 ? 's' : ''}
                      {entry.lateralization && ` · ${entry.lateralization}`}
                      {entry.umn_lmn_pattern && ` · ${entry.umn_lmn_pattern}`}
                    </p>

                    {/* Changes from previous */}
                    {entry.new_locations?.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-[#D94F4F]">
                        <AlertTriangle className="w-3 h-3" />
                        New locations: {entry.new_locations.join(', ')}
                      </div>
                    )}
                    {entry.resolved_locations?.length > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-[#4B6651]">
                        <TrendingUp className="w-3 h-3" />
                        Resolved: {entry.resolved_locations.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LesionProgression;
